import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card } from '@atoms/Card';
import { Spinner } from '@atoms/Spinner';
import { SalesChart } from '@molecules/SalesChart';
import { TransactionDetailModal } from '@molecules/TransactionDetailModal';
import { financeService } from '@services/financeService';
import { formatCurrency, formatDate } from '@utils/formatters';
import type { Transaction } from '@/types';

export function AccountingSection() {
  const [selectedWholesalerId, setSelectedWholesalerId] = useState('');
  const [detailTx, setDetailTx] = useState<Transaction | null>(null);

  const { data: accounts, isLoading: accountsLoading } = useQuery({
    queryKey: ['restaurant', 'cari'],
    queryFn: () => financeService.getRestaurantAccounts(),
  });

  const { data: history, isLoading: historyLoading } = useQuery({
    queryKey: ['restaurant', 'cari-history', selectedWholesalerId],
    queryFn: () => financeService.getRestaurantHistory(selectedWholesalerId),
    enabled: Boolean(selectedWholesalerId),
  });

  const { data: chart, isLoading: chartLoading } = useQuery({
    queryKey: ['restaurant', 'finance-chart', selectedWholesalerId],
    queryFn: () => financeService.getSpendingChart(selectedWholesalerId || undefined),
    enabled: Boolean(selectedWholesalerId),
  });

  if (accountsLoading) return <Spinner />;

  const selectedAccount = accounts?.find((a) => a.wholesalerId === selectedWholesalerId);

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Cari Hesaplarım</h2>
      <p className="text-sm text-gray-500">Borç durumunuzu görüntüleyin (salt okunur).</p>

      <Card>
        <label className="mb-1 block text-sm font-medium">Toptancı</label>
        <select
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          value={selectedWholesalerId}
          onChange={(e) => setSelectedWholesalerId(e.target.value)}
        >
          <option value="">Toptancı seçin</option>
          {(accounts ?? []).map((a) => (
            <option key={a.id} value={a.wholesalerId}>
              {a.wholesalerName} — {formatCurrency(a.balance)}
            </option>
          ))}
        </select>
      </Card>

      {selectedWholesalerId && (
        <>
          <Card>
            <p className="text-sm text-gray-500">Bakiye</p>
            <p className="text-3xl font-bold text-red-600">
              {formatCurrency(history?.balance ?? selectedAccount?.balance ?? 0)}
            </p>
          </Card>

          {chartLoading ? <Spinner /> : (
            <SalesChart data={chart ?? []} title="Harcama Grafiği (7 Gün)" valueLabel="Harcama" />
          )}

          <div className="space-y-2">
            <h3 className="font-medium">İşlem Geçmişi</h3>
            {historyLoading ? (
              <Spinner />
            ) : (history?.transactions ?? []).length === 0 ? (
              <p className="text-sm text-gray-500">Henüz işlem yok.</p>
            ) : (
              (history?.transactions ?? []).map((tx) => (
                <Card
                  key={tx.id}
                  className="flex cursor-pointer justify-between text-sm hover:bg-gray-50"
                  onClick={() => setDetailTx(tx)}
                >
                  <div>
                    <p>{tx.description ?? (tx.type === 'debt' ? 'Borç' : 'Alacak')}</p>
                    <p className="text-xs text-gray-500">{formatDate(tx.createdAt)}</p>
                  </div>
                  <span className={tx.type === 'debt' ? 'text-red-600' : 'text-green-600'}>
                    {tx.type === 'debt' ? '-' : '+'}
                    {formatCurrency(tx.amount)}
                  </span>
                </Card>
              ))
            )}
          </div>
        </>
      )}

      {!selectedWholesalerId && (accounts ?? []).length === 0 && (
        <p className="text-sm text-gray-500">Henüz cari hesabınız bulunmuyor.</p>
      )}

      <TransactionDetailModal transaction={detailTx} onClose={() => setDetailTx(null)} />
    </div>
  );
}
